<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="
      referencesForFullTextReviewConflictReviewConnection
    "
    :pending-connection-query="
      $apollo.queries
        .referencesForFullTextReviewConflictReviewConnection
    "
    :history-connection="
      referencesByFullTextReviewConflictResolutionHistoryConnection
    "
    :history-connection-query="
      $apollo.queries
        .referencesByFullTextReviewConflictResolutionHistoryConnection
    "
    :search-input.sync="searchInput"
    :revoke-vote-func="revokeOutcome"
    :get-vote-by-current-user-func="getResolutionByCurrentUser"
    :filtering-input="filteringInput"
    full-text-review
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <page-content-placeholder
        icon="check"
        icon-color="success"
        prompt-text="Complete"
      >
        <template #message>
          All conflicts have been resolved.
        </template>
      </page-content-placeholder>
    </template>
  </screening-workflow-panel>
</template>

<script>
import PageContentPlaceholder from '@/components/Common/PageContentPlaceholder';
import ScreeningWorkflowPanel from '@/components/ScreeningPageV2/ScreenTab/ScreeningWorkflowPanel';
import {
  GetReferencesForFullTextReviewConflictReview,
  GetReferencesByFullTextReviewConflictResolutionHistory,
} from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;

export default {
  name: 'ReviewTabConflicting',
  components: {
    PageContentPlaceholder,
    ScreeningWorkflowPanel,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    referencesForFullTextReviewConflictReviewConnection: null,
    referencesByFullTextReviewConflictResolutionHistoryConnection: null,
  }),
  apollo: {
    referencesForFullTextReviewConflictReviewConnection: {
      query: GetReferencesForFullTextReviewConflictReview,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesForFullTextReviewConflictReview
        ),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    referencesByFullTextReviewConflictResolutionHistoryConnection: {
      query: GetReferencesByFullTextReviewConflictResolutionHistory,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesByFullTextReviewConflictResolutionHistory
        ),
      skip() {
        return (
          !this.review?.id ||
          !this.referencesForFullTextReviewConflictReviewConnection
        );
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (
        active &&
        this.referencesForFullTextReviewConflictReviewConnection
      ) {
        this.$apollo.queries.referencesForFullTextReviewConflictReviewConnection.refetch();
        this.$apollo.queries.referencesByFullTextReviewConflictResolutionHistoryConnection.refetch();
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesForFullTextReviewConflictReviewConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      revokeOutcome: 'fullTextReviewOutcome/removeConflictResolution',
    }),
    getResolutionByCurrentUser(reference) {
      return (
        reference?.fullTextReviewOutcome?.resolvedBy?.researcher
          .id === this.currentResearcher.id &&
        reference?.fullTextReviewOutcome
      );
    },
  },
};
</script>
